<template>
  <div v-if="open"
       :ref="`datepicker-${fieldName}`"
       class="date-picker position-fixed d-flex flex-column bg-sachlich-white"
       @dblclick.stop
       @click.stop>
    <div class="d-flex justify-content-center align-items-center my-1">
      <div class="arrow ms-5"
           :class="{ active: prevMonthExists }"
           @click.stop="decrementCurrentMonth"
           @dblclick.stop>
        <i class="fa fa-angle-left" />
      </div>
      <div style="flex: 1">
        <div class="year-month fw-bold text-center color-expinet-blue">
          {{ displayMonth }}
        </div>
        <div class="year-month fw-bold text-center color-expinet-blue">
          {{ displayYear }}
        </div>
      </div>
      <div class="arrow me-5"
           :class="{ active: nextMonthExists }"
           @click.stop="incrementCurrentMonth"
           @dblclick.stop>
        <i class="fa fa-angle-right" />
      </div>
    </div>
    <div class="date-picker-days d-flex flex-column">
      <div class="weekdays fw-bold text-center mt-3">
        <ul class="d-flex">
          <li class="d-inline-block">MO</li>
          <li class="d-inline-block">DI</li>
          <li class="d-inline-block">MI</li>
          <li class="d-inline-block">DO</li>
          <li class="d-inline-block">FR</li>
          <li class="d-inline-block">SA</li>
          <li class="d-inline-block">SO</li>
        </ul>
      </div>
      <div class="days fw-bold text-center mt-2">
        <ul>
          <li v-for="(day, dayIndex) in dayList"
              :key="dayIndex"
              class="day position-relative"
              :class="cssClasses(day)"
              @click.stop="selectDay(day)">
            {{ day.date.date() }}
            <div v-if="day.holidays.length"
                 class="holidays-tooltip position-absolute fw-normal p-1">
              <div v-for="holiday in day.holidays"
                   :key="`${holiday.name}-${holiday.countryCode}`">
                <div class="fw-bold">
                  {{ holiday.name }} ({{ holiday.countryCode }})
                </div>

                <div v-if="!!nationwideText(holiday.countryCode, holiday)">
                  {{ nationwideText(holiday.countryCode, holiday) }}
                </div>
                <template v-else>
                  <div v-for="region in holiday.regions"
                       :key="`${holiday.name}-${holiday.countryCode}-${region}`">
                    {{ region }}
                  </div>
                </template>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="d-flex flex-column m-2">
      <div v-if="showHolidayFilterError"
           class="mx-auto my-1 color-aida-red">
        Schulferien zurzeit nicht verfügbar
      </div>
      <ExpiButton label-id="Schulferienfilter"
                  :click="openHolidayFilterModal" />
    </div>

    <Modal :show="isHolidayFilterModalOpen"
           :close="closeHolidayFilterModal"
           size="big">
      <div class="d-flex">
        <div v-for="[countryCode, countryData] of Object.entries(holidaysByCountry)"
             :key="countryCode"
             class="d-flex flex-column p-2">
          <div class="d-flex justify-content-between align-items-center me-2">
            <ToggleButton :checked="countryData.selected"
                          :translate-with-default="getText"
                          :label-id="countryData.countryName"
                          :small="true"
                          button-color="expi-blue"
                          label-font-css="fw-bold"
                          @click="toggleStateFilterSettingByCountryValue(countryCode, countryData.selected)" />
          </div>

          <hr class="devider my-2 w-100 opacity-100">

          <div class="d-flex flex-wrap"
               :class="{ 'toggle-extra-width': countryCode === 'CH' }">
            <div v-for="[stateCode, stateData] of Object.entries(countryData.federalStates)"
                 :key="`${countryCode}-${stateCode}`"
                 class="toggle d-flex justify-content-between align-items-center">
              <ToggleButton :checked="stateData.selected"
                            :translate-with-default="getText"
                            :label-id="stateData.stateName"
                            :small="true"
                            button-color="expi-blue"
                            @click="toggleStateFilterSetting(`${countryCode}-${stateCode}`, stateData.selected)" />
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center">
        Bitte beachten Sie, dass die Ferien in manchen Regionen abweichen können. Bitte überprüfen Sie Ihre Ferientermine vor Buchung.
      </div>
    </Modal>
  </div>
</template>

<script lang="ts">
import { useFilterStore } from '@/store'
import { HolidaysByCountry } from '@/store/filter/interface'
import { ToolTipHolidays } from '@/store/ui/interfaces'
import ToggleButton from '@gecko-akh/aos-toggle-button'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import arraySupport from 'dayjs/plugin/arraySupport'
import CustomParseFormat from 'dayjs/plugin/customParseFormat'
import weekday from 'dayjs/plugin/weekday'
import { cloneDeep } from 'lodash-es'
import { mapActions, mapState } from 'pinia'
import { DEFAULT_DATE_FORMAT, VALID_DATE_FORMATS } from '../../general/helper'
import ExpiButton from './ExpiButton.vue'
import Modal from './Modal.vue'

dayjs.locale('de')
dayjs.extend(weekday)
dayjs.extend(arraySupport)
dayjs.extend(CustomParseFormat)

export default {
  components: {
    Modal,
    ToggleButton,
    ExpiButton
  },
  props: {
    fieldName: { type: String, default: '' },
    fieldValue: { type: String, default: '' },
    valueChanged: {
      type: Function,
      default: () => {
        console.error('You need to implement an "valueChanged" event handler.') // eslint-disable-line no-console
      },
    },
    picked: {
      type: Function,
      default: () => {
        console.error('You need to implement an "picked" event handler.') // eslint-disable-line no-console
      },
    },
    placeholder: { type: String, default: '' },
    open: { type: Boolean, default: false },
    minDate: { type: dayjs.Dayjs, default: null },
    maxDate: { type: dayjs.Dayjs, default: null },
  },

  data: () => {
    return {
      currentYear: undefined as number | undefined,
      currentMonth: undefined as number | undefined,
      currentDay: undefined as number | undefined,
      isHolidayFilterModalOpen: false,
    }
  },

  computed: {
    ...mapState(useFilterStore, {
      holidaysByCountry: store => store.holidaysByCountry,
      showHolidayFilterError: store => store.showHolidayFilterError,
    }),
    fieldValueAsMoment() {
      return dayjs(this.fieldValue, DEFAULT_DATE_FORMAT)
    },
    currentValue() {
      return dayjs([this.currentYear, this.currentMonth, this.currentDay])
    },
    displayYear() {
      return this.currentYear
    },
    displayMonth() {
      return this.currentValue.format('MMMM').toUpperCase()
    },
    prevMonthExists() {
      const minYear = this.minDate.year()
      const minMonth = this.minDate.month()
      return (!!this.currentYear && minYear < this.currentYear) || (!!this.currentMonth && minMonth < this.currentMonth)
    },
    nextMonthExists() {
      const maxYear = this.maxDate.year()
      const maxMonth = this.maxDate.month()

      return (
        !!this.currentYear && maxYear > this.currentYear) ||
        (typeof this.currentMonth === 'number' && isFinite(this.currentMonth) && maxMonth > this.currentMonth)
    },
    dayList() {
      const firstDay = this.currentValue.clone().date(1)
      const lastDay = this.currentValue.clone().endOf('month')
      const monthLength = lastDay.diff(firstDay, 'day') + 1
      const fillBefore = firstDay.weekday()
      const fillAfter = 6 - lastDay.weekday()
      const dayList = []

      for (let i = -fillBefore; i < monthLength + fillAfter; ++i) {
        const day = {
          date: firstDay.clone().add(i, 'day'),
          outside: i < 0 || i >= monthLength,
          holidays: [] as ToolTipHolidays[],
        }
        dayList.push(day)
      }

      for (const day of dayList) {
        if (day.date.isBefore(this.minDate, 'day')) {
          day.outside = true
        }

        if (!day.outside) {
          day.holidays = this.mapHolidaysForDate(day.date)
        }
      }

      return dayList
    },
  },

  watch: {
    fieldValue() {
      let date = this.minDate

      if (!!this.fieldValue) {
        for (const fmt of VALID_DATE_FORMATS) {
          if (dayjs(this.fieldValue, fmt.format, true).isValid()) {
            date = dayjs(this.fieldValue, fmt.format, true)
          }

          if ((fmt.autoComplete && date.isBefore(dayjs())) || date.isBefore(this.minDate)) {
            while (date.isBefore(dayjs()) || date.isBefore(this.minDate)) {
              date = date.add(1, 'year')
            }
            break
          }
        }
      }

      this.currentYear = date.year()
      this.currentMonth = date.month()
      this.currentDay = date.date()
    },
    open(newValue) {
      if (!window.addEventListener) {
        return
      }

      this.isHolidayFilterModalOpen = false

      if (!!newValue) {
        window.addEventListener('click', this.closeHandler)
      } else {
        window.removeEventListener('click', this.closeHandler)
      }
    },
    minDate: {
      handler() {
        if (!this.fieldValue) {
          this.currentYear = this.minDate.year()
          this.currentMonth = this.minDate.month()
          this.currentDay = this.minDate.date()
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (!!this.fieldValue) {
      const m = dayjs(this.fieldValue, DEFAULT_DATE_FORMAT)
      this.currentYear = m.year()
      this.currentMonth = m.month()
      this.currentDay = m.date()
    } else {
      this.currentYear = this.minDate.year()
      this.currentMonth = this.minDate.month()
      this.currentDay = this.minDate.date()
    }
  },
  methods: {
    ...mapActions(useFilterStore, {
      setHolidayCountrySelectedByStates: 'setHolidayCountrySelectedByStates',
      setHolidayStateSelectedByCountry: 'setHolidayStateSelectedByCountry',
      setHolidayStateSelected: 'setHolidayStateSelected',
    }),

    cssClasses(day: any) {
      const classes = []

      if (this.dayIsCurrentValue(day.date)) {
        classes.push('selected color-sachlich-white')
      }

      if (day.outside) {
        classes.push('dead')
      } else if (!!day.holidays.length) {
        classes.push('availableHoliday')
      }

      return classes.join(' ')
    },
    decrementCurrentMonth() {
      if (this.prevMonthExists) {
        const current = this.currentValue.clone()

        this.currentDay = current.date()
        this.currentMonth = current.subtract(1, 'month').month()
        this.currentYear = this.currentMonth === 11 ? current.subtract(1, 'year').year() : current.year()
      }
    },
    incrementCurrentMonth() {
      if (this.nextMonthExists) {
        const current = this.currentValue.clone()

        this.currentDay = current.date()
        this.currentMonth = current.add(1, 'month').month()
        this.currentYear = this.currentMonth === 0 ? current.add(1, 'year').year() : current.year()
      }
    },
    dayIsCurrentValue(day: dayjs.Dayjs) {
      return day.diff(this.fieldValueAsMoment, 'day') === 0
    },
    selectDay(day: any) {
      if (!day.outside) {
        this.picked()
        this.currentYear = day.date.year()
        this.currentMonth = day.date.month()
        this.currentDay = day.date.date()
        this.valueChanged(this.fieldName, this.currentValue.format(DEFAULT_DATE_FORMAT))
      }
    },
    closeHandler(event: MouseEvent) {
      if (!this.open) {
        return
      }

      const datePicker = this.$refs[`datepicker-${this.fieldName}`] as HTMLElement
      const rect = !!datePicker ? datePicker.getClientRects()[0] : null

      if (
        !!rect &&
        (event.clientX < rect.left ||
          event.clientX > rect.left + rect.width ||
          event.clientY < rect.top ||
          event.clientY > rect.top + rect.height)
      ) {
        let date

        for (const fmt of VALID_DATE_FORMATS) {
          date = dayjs(this.fieldValue, fmt.format, true)

          if (date.isValid()) {
            this.valueChanged(this.fieldName, date.format(DEFAULT_DATE_FORMAT))
            break
          }
        }

        this.picked()
      }
    },

    findActiveHolidays(date: dayjs.Dayjs): HolidaysByCountry {
      const filteredHolidaysByCountry: HolidaysByCountry = cloneDeep(this.holidaysByCountry)

      for (const [country, holidaysByCountry] of Object.entries(this.holidaysByCountry)) {
        if (!holidaysByCountry.holidays.length) break

        const filteredHolidays = holidaysByCountry.holidays.filter(holiday => {
          const isInDateRange = !date.isBefore(dayjs(holiday.startDate)) && !date.isAfter(dayjs(holiday.endDate))

          const federalState = Object.entries(holidaysByCountry.federalStates).find(state => {
            return !!holiday.regions?.length && holiday.regions?.some(region => region.code === state[0])
          })

          return isInDateRange && (federalState?.[1]?.selected || holidaysByCountry.selected)
        })

        filteredHolidaysByCountry[country].holidays = filteredHolidays
      }

      return filteredHolidaysByCountry
    },

    mapHolidaysForDate(date: dayjs.Dayjs): ToolTipHolidays[] {
      const toolTipHolidays: ToolTipHolidays[] = []

      for (const [country, holidays] of Object.entries(this.findActiveHolidays(date))) {
        holidays.holidays.map(holiday => {
          const mappedHoliday = {
            countryCode: country,
            name: holiday.name,
            regions: holiday.regions ? holiday.regions.map(region => region.name) : [],
            nationwide: holiday.nationwide,
          }

          const foundToolTipHolidays = toolTipHolidays.find(
            holiday => holiday.countryCode === mappedHoliday.countryCode && holiday.name === mappedHoliday.name
          )

          if (!!foundToolTipHolidays) {
            foundToolTipHolidays.regions = foundToolTipHolidays.regions.concat(mappedHoliday.regions)
            foundToolTipHolidays.regions = [...new Set(foundToolTipHolidays.regions)]
          } else {
            toolTipHolidays.push(mappedHoliday)
          }
        })
      }

      return toolTipHolidays
    },

    openHolidayFilterModal() {
      this.isHolidayFilterModalOpen = true
    },

    closeHolidayFilterModal() {
      this.isHolidayFilterModalOpen = false
    },

    toggleStateFilterSettingByCountryValue(fieldName: string, fieldValue: boolean) {
      this.setHolidayStateSelectedByCountry(fieldName, !fieldValue)
    },

    toggleStateFilterSetting(fieldName: string, fieldValue: boolean) {
      const codes = fieldName.split('-')
      this.setHolidayStateSelected({
        countryCode: codes[0],
        stateCode: codes[1],
        value: !fieldValue,
      })

      this.setHolidayCountrySelectedByStates(codes[0])
    },

    // TODO: replace with correct method when AEM is used
    getText: (labelId: string) => {
      return labelId
    },

    nationwideText: (country: string, holiday: ToolTipHolidays) => {
      const nationwide = holiday.nationwide

      if (country === 'DE' && (nationwide || holiday.regions.length >= 16)) {
        return 'Deutschlandweit'
      } else if (country === 'AT' && (nationwide || holiday.regions.length >= 9)) {
        return 'gesamt Österreich'
      } else if (country === 'CH' && (nationwide || holiday.regions.length >= 26)) {
        return 'gesamte Schweiz'
      }

      return ''
    },

    getTranslateText: (label: string) => {
      return label
    }
  },
}
</script>

<style lang="scss" scoped>
@import '../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

@mixin clear() {

  &:before,
  &:after {
    content: ' ';
    display: table;
  }

  &:after {
    clear: both;
  }
}

.date-picker {
  width: 245px;
  z-index: 4;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
  border: 2px solid $color-expinet-blue;
}

.year-month {
  font-size: $default-font-size;
}

.arrow {
  font-size: 25px;
  color: $color-expinet-gray;

  &.active {
    color: $color-expinet-blue;
    cursor: pointer;
  }

  &:hover {
    color: $color-expinet-blue-h;
  }
}

.date-picker-days {
  flex: 1;
  border-top: 1px solid $color-expinet-gray;
}

.weekdays {
  color: $color-aida-black;
  font-size: $default-font-size;

  li {
    width: 35px;
    height: 20px;
  }
}

.days {
  color: $color-aida-black;
  font-size: $default-font-size;

  li {
    display: inline-block;
    padding: 0 2px;
    margin: 2px 0;
    width: 33px;
    height: 31px;
    line-height: 30px;
    border-radius: 50%;
    cursor: pointer;

    &:hover {
      color: $color-aida-white;
      background-color: $color-expinet-blue;

      .holidays-tooltip {
        display: block;
      }
    }

    &.dead {
      color: $color-expinet-gray;
      cursor: default;

      &:hover {
        background-color: transparent;
      }
    }

    &.selected {
      background-color: $color-expinet-blue;
    }

    &.availableHoliday:not(:hover, .selected) {
      background-color: $color-expinet-blue-light;
      border-radius: 0%;
    }
  }
}

.toggle-extra-width {
  width: 440px;
}

.toggle {
  width: 200px;
  padding: 3px;
}

.devider {
  color: $color-expinet-gray;
  border-color: $color-expinet-gray;
}

.holidays-tooltip {
  display: none;
  z-index: 10;
  color: $color-aida-black;
  min-width: 200px;
  line-height: 20px;
  background-color: $color-aida-white;
  box-shadow: 0 3px 9px 0 rgba(0, 0, 0, 0.3);
  border: 1px solid $color-expinet-gray;

  &:after {
    background-color: $color-aida-white;
    content: '';
    display: block;
    position: absolute;
    width: 12px;
    height: 12px;
    transform: rotate(45deg);
    top: -6px;
    left: 7px;
    border-top: 1px solid $color-expinet-gray;
    border-left: 1px solid $color-expinet-gray;
  }
}
</style>
